import { DownloadLink } from "../DownloadLink";
import { useEffect, useRef, useState, type FocusEvent, type RefObject } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useLocation } from "react-router-dom";
import { ArrowUpRight, ChartNoAxesCombined, CircleHelp, Download, Layers, MessagesSquare, MonitorPlay, Play, Plug, Sparkles, type LucideIcon } from "lucide-react";
import { dentalPageSections, getDentalPageProgress } from "../../dentalPageProgress";
import { getDentalSectionAnchorTop } from "../../dentalSectionNavigation";
import { downloadPath } from "../../routes";
import logoMarkSrc from "../../assets/clawease-logo-mark-vector.svg";

const navigationIcons: Record<string, LucideIcon> = {
  "front-desk-story": Sparkles,
  "dental-practice-impact": ChartNoAxesCombined,
  "conversation-dashboard": MessagesSquare,
  "dental-integrations": Plug,
  "dental-pms-setup": MonitorPlay,
  "workflow-console": Play,
  pricing: Layers,
  "dental-faq": CircleHelp,
  "dental-download": Download,
};
const navigationItems = [
  ...dentalPageSections.slice(1),
  { id: "dental-download", label: "Learn it more", shortLabel: "Learn" },
];
const MotionLink = motion.create(DownloadLink);
const ringCircumference = 2 * Math.PI * 26;

function DentalNavigationItem({ section, current, influence, expanded, reducedMotion, to, onFocus, onNavigate }: {
  section: { id: string; label: string; shortLabel: string };
  current: boolean;
  influence: number;
  expanded: boolean;
  reducedMotion: boolean;
  to?: string;
  onFocus: (keyboard: boolean) => void;
  onNavigate?: () => void;
}) {
  const suffixRef = useRef<HTMLSpanElement>(null);
  const [suffixWidth, setSuffixWidth] = useState(0);
  const Icon = navigationIcons[section.id];

  useEffect(() => {
    const text = suffixRef.current;
    if (!text) return;
    const measure = () => setSuffixWidth(text.getBoundingClientRect().width);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(text);
    return () => observer.disconnect();
  }, []);

  const animatedProps = {
    className: "dental-page-navigation-stop",
    "data-expanded": expanded || undefined,
    initial: false as const,
    animate: { "--dental-nav-influence": influence, "--dental-nav-detail": expanded ? 1 : 0 },
    transition: { duration: reducedMotion ? 0 : .3, ease: [.22, .8, .25, 1] as const },
    onFocus: (event: FocusEvent<HTMLElement>) => onFocus(event.currentTarget.matches(":focus-visible")),
  };
  const content = (
    <>
      <Icon className="dental-page-navigation-icon" aria-hidden="true" />
      <span className="dental-page-navigation-label" aria-hidden="true">
        <span className="dental-page-navigation-keyword">{section.shortLabel}</span>
        <span className="dental-page-navigation-suffix" style={{ width: `calc(${suffixWidth}px * var(--dental-nav-detail))` }}>
          <span ref={suffixRef}>{section.label.slice(section.shortLabel.length)}</span>
        </span>
      </span>
      <ArrowUpRight className="dental-page-navigation-arrow" aria-hidden="true" />
    </>
  );

  return to ? (
    <MotionLink {...animatedProps} to={to} aria-label={section.label} data-download>
      {content}
    </MotionLink>
  ) : (
    <motion.button
      {...animatedProps}
      type="button"
      aria-label={`Go to ${section.label}`}
      aria-controls={section.id}
      aria-current={current ? "location" : undefined}
      onClick={onNavigate}
    >
      {content}
    </motion.button>
  );
}

export function DentalPageNavigation({ mainRef }: { mainRef: RefObject<HTMLElement | null> }) {
  const { pathname, search } = useLocation();
  const [progress, setProgress] = useState({ activeIndex: 0, percent: 0, sectionPercent: 0 });
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [focusIndex, setFocusIndex] = useState<number | null>(null);
  const reducedMotion = !!useReducedMotion();
  const expandedIndex = progress.activeIndex > 0 ? hoverIndex ?? focusIndex : null;

  useEffect(() => {
    if (progress.activeIndex === 0) { setHoverIndex(null); setFocusIndex(null); }
  }, [progress.activeIndex]);

  useEffect(() => {
    const main = mainRef.current;
    const browser = main?.ownerDocument.defaultView;
    if (!main || !browser) return;
    const sections = Array.from(main.querySelectorAll<HTMLElement>(":scope > section"));
    let frame = 0;
    const update = () => {
      frame = 0;
      const bounds = sections.map((section) => {
        const rect = section.getBoundingClientRect();
        return { top: rect.top + browser.scrollY, bottom: rect.bottom + browser.scrollY };
      });
      const next = getDentalPageProgress(
        bounds, browser.scrollY, browser.innerHeight, main.ownerDocument.documentElement.scrollHeight,
      );
      setProgress((previous) => previous.activeIndex === next.activeIndex && previous.percent === next.percent && previous.sectionPercent === next.sectionPercent ? previous : next);
    };
    const schedule = () => {
      if (!frame) frame = browser.requestAnimationFrame(update);
    };
    update();
    browser.addEventListener("scroll", schedule, { passive: true });
    browser.addEventListener("resize", schedule);
    browser.addEventListener("pageshow", schedule);
    const observer = new ResizeObserver(schedule);
    observer.observe(main);
    sections.forEach((section) => observer.observe(section));
    return () => {
      browser.cancelAnimationFrame(frame);
      browser.removeEventListener("scroll", schedule);
      browser.removeEventListener("resize", schedule);
      browser.removeEventListener("pageshow", schedule);
      observer.disconnect();
    };
  }, [mainRef]);

  const goToSection = (id: string) => {
    const main = mainRef.current;
    const browser = main?.ownerDocument.defaultView;
    const section = main?.ownerDocument.getElementById(id);
    if (!main || !browser || !section) return;
    const maxScroll = Math.max(0, main.ownerDocument.documentElement.scrollHeight - browser.innerHeight);
    const target = id === dentalPageSections[0].id ? 0 : getDentalSectionAnchorTop(section, browser.scrollY, browser.innerHeight);
    browser.scrollTo({
      top: Math.max(0, Math.min(target, maxScroll)),
      behavior: browser.matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth",
    });
  };

  return (
    <>
      <aside className="dental-page-navigation" data-visible={progress.activeIndex > 0}>
        <div className="dental-page-navigation-brand">
          <button type="button" aria-label="ClawEase — back to top" onClick={() => goToSection("dental-overview")}>
            <svg className="dental-page-navigation-ring" viewBox="0 0 60 60" role="progressbar" aria-label="Page viewing progress" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress.percent}>
              <circle className="dental-page-navigation-ring-track" cx="30" cy="30" r="26" />
              <circle className="dental-page-navigation-ring-fill" cx="30" cy="30" r="26" strokeDasharray={ringCircumference} strokeDashoffset={ringCircumference * (1 - progress.percent / 100)} />
            </svg>
            <img src={logoMarkSrc} alt="" width="30" height="30" />
          </button>
        </div>
        <nav aria-label="On this page">
          <ol
            data-engaged={expandedIndex !== null || undefined}
            onPointerMove={(event) => {
              if (event.pointerType === "touch") return;
              const rows = Array.from(event.currentTarget.children);
              // Include the gaps and extended rows so moving vertically along
              // the wave never crosses a dead zone and collapses the card.
              const offset = rows.findIndex((row) => event.clientY < row.getBoundingClientRect().bottom);
              setHoverIndex((offset < 0 ? rows.length - 1 : offset) + 1);
            }}
            onPointerLeave={() => setHoverIndex(null)}
            onBlur={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) setFocusIndex(null);
            }}
            onKeyDown={(event) => {
              if (event.key === "Escape") { setHoverIndex(null); setFocusIndex(null); }
            }}
          >
            {navigationItems.map((section, offset) => {
              const index = offset + 1;
              const isDownload = section.id === "dental-download";
              const distance = expandedIndex === null ? Infinity : Math.abs(index - expandedIndex);
              const influence = distance > 2 ? 0 : Math.exp(-.5 * (distance / .93) ** 2);
              return (
                <li key={section.id}>
                  <DentalNavigationItem
                    section={section}
                    current={!isDownload && progress.activeIndex === index}
                    influence={influence}
                    expanded={expandedIndex === index}
                    reducedMotion={reducedMotion}
                    to={isDownload ? downloadPath("en", "dental_navigation", undefined, search, pathname) : undefined}
                    onFocus={(keyboard) => setFocusIndex(keyboard ? index : null)}
                    onNavigate={isDownload ? undefined : () => goToSection(section.id)}
                  />
                </li>
              );
            })}
          </ol>
        </nav>
      </aside>
      <div className="dental-page-progress-mobile" data-visible={progress.activeIndex > 0} aria-hidden="true">
        <div style={{ transform: `scaleX(${progress.percent / 100})` }} />
      </div>
    </>
  );
}
